import { useEffect, useMemo } from 'react';

import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
} from '../weborder/Api';
import { Routes } from '../weborder/Routes';
import { CollectionMap, DeliveryMap } from '../weborder/Store';

interface UseRestaurantDetails {
  data: CollectionRestaurantDetail | DeliveryRestaurantDetail | null;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useRestaurantDetails = (
  getCollectionLocationAreaData: (prId: number) => void,
  getRestaurantDetails: (
    vrId: number,
    type: 'collection' | 'delivery',
    id: number,
  ) => void,
  collectionDetails: CollectionMap,
  deliveryDetails: DeliveryMap,
  routeParams: Routes['params'],
): UseRestaurantDetails => {
  const { type } = routeParams;
  const isCollection = type === 'collection';
  const isDelivery = type === 'delivery';

  const vrId = Number(routeParams.vrid);
  const prId = isCollection ? Number(routeParams.id) : undefined;
  const deliveryLocationId = isDelivery ? Number(routeParams.id) : undefined;

  const collectionStatus = collectionDetails[prId]?.status;
  const deliveryStatus =
    deliveryDetails[`del-${vrId}-${deliveryLocationId}`]?.status;

  const restaurantDetail = useMemo(() => {
    if (isCollection && !!collectionStatus) {
      return collectionDetails[prId];
    }

    if (isDelivery && !!deliveryStatus) {
      return deliveryDetails[`del-${vrId}-${deliveryLocationId}`];
    }

    return { data: null, status: 'pending' };
  }, [collectionDetails, deliveryStatus]);

  const isLoading = restaurantDetail.status === 'pending';
  const isSuccess = restaurantDetail.status === 'success';
  const restaurantDetailData = isSuccess ? restaurantDetail.data : null;

  useEffect(() => {
    if (!isSuccess) {
      // * Redux state - collectionRestaurants[prId] | deliveryRestaurants[del-vrId-deliveryLocationId]
      // - set restaurant collection/delivery details
      const id = isDelivery ? deliveryLocationId : prId;
      getRestaurantDetails(vrId, type, id);
    }
  }, []);

  useEffect(() => {
    if (isSuccess && isCollection) {
      // * Redux state - collectionRestaurants[prId].data.LocationAreas
      // - set restaurant collection details location areas
      getCollectionLocationAreaData(prId);
    }
  }, [isSuccess]);

  return {
    data: restaurantDetailData,
    isLoading,
    isSuccess,
  };
};
